import React from 'react';
import { FormattedMessage } from 'react-intl';
import { InterPoint } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  Chip,
  type ChipColorTypes,
  Typography,
} from '@saviynt/design-system';

import { CF_NEU_BLD, CF_SEC_MED } from '../../constants';
import { getLogoEndpointUrl } from '../../utils';

import './Shared.css';

interface ViolationCenterContentProps {
  riskName: string;
  rulesetName: string;
}

/**
 * Shared
 */
function ViolationCenterContent({
  riskName,
  rulesetName,
}: ViolationCenterContentProps) {
  return (
    <Box tag='div' className='ViolationCenterContent'>
      <Typography kind='h3' color={CF_NEU_BLD} lineLimit={1} isNoWrap>
        {riskName}
      </Typography>
      {rulesetName.trim().length > 0 && (
        <Typography kind='body1' color={CF_SEC_MED} lineLimit={1} isNoWrap>
          <FormattedMessage
            id='i0xbnU'
            defaultMessage='({rulesetName})'
            description='Text to display the ruleset name associated with a risk in the ViolationCenterContent component'
            values={{ rulesetName }}
          />
        </Typography>
      )}
    </Box>
  );
}

type SeverityLevel =
  | 'VERY_LOW'
  | 'LOW'
  | 'MEDIUM'
  | 'MODERATE'
  | 'HIGH'
  | 'CRITICAL';

const RISK_PRIORITY_MAP = {
  VERY_LOW: { text: 'Very Low', color: 'success' },
  LOW: { text: 'Low', color: 'success' },
  MEDIUM: { text: 'Moderate', color: 'warning' },
  MODERATE: { text: 'Moderate', color: 'warning' },
  HIGH: { text: 'High', color: 'orange' },
  CRITICAL: { text: 'Critical', color: 'critical' },
} satisfies Record<SeverityLevel, { text: string; color: ChipColorTypes }>;

interface RiskPriorityChipProps {
  priority: string | null;
  riskScore: number | null;
}

/**
 * For risk priority chip
 */
function SodSeverityChip({ priority, riskScore }: RiskPriorityChipProps) {
  if (!priority || !Object.keys(RISK_PRIORITY_MAP).includes(priority)) {
    return null;
  }

  const label = `${RISK_PRIORITY_MAP[priority as SeverityLevel].text}`;
  const { color } = RISK_PRIORITY_MAP[priority as SeverityLevel];

  const getRiskScore = () => {
    if (riskScore === null || riskScore === 0) {
      return label;
    }

    return `${label} (${riskScore})`;
  };

  return (
    <Chip
      size='xSmall'
      kind='subtleFilled'
      label={getRiskScore()}
      color={color}
      isInteractionDisabled
    />
  );
}

interface FunctioncenterContentProps {
  fnName: string;
  accountName: string;
  accountLogo: string;
}

/**
 * FunctionCenterContent
 */
function FunctionCenterContent({
  fnName,
  accountName,
  accountLogo,
}: FunctioncenterContentProps) {
  return (
    <Box tag='div' className='FunctionCenterContent'>
      <Typography kind='body1-bold' color={CF_NEU_BLD} lineLimit={1} isNoWrap>
        {fnName}
      </Typography>
      <InterPoint />
      <Box tag='div' className='ServiceAccountListItem-contentApplication'>
        <ApplicationIcon size='xxSmall' src={getLogoEndpointUrl(accountLogo)} />
        <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1} isNoWrap>
          {accountName}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * ParentEntitlementCenterContent
 */
function ParentEntitlementCenterContent() {
  return (
    <Box tag='div' className='ParentEntitlementCenterContent'>
      <Typography kind='body3' color={CF_SEC_MED} lineLimit={1} isNoWrap>
        <FormattedMessage
          id='za63Nh'
          defaultMessage='Parent Entitlement'
          description='Text to indicate that the entitlement displayed in the ParentEntitlementCenterContent component is a parent entitlement'
        />
      </Typography>
      <Typography kind='body1-bold' color={CF_NEU_BLD} lineLimit={1} isNoWrap>
        <FormattedMessage
          id='sTPUuU'
          defaultMessage='Entitlement Name'
          description='Text to indicate the name of the entitlement in the ParentEntitlementCenterContent component'
        />
      </Typography>
    </Box>
  );
}

export {
  FunctionCenterContent,
  ParentEntitlementCenterContent,
  SodSeverityChip,
  ViolationCenterContent,
};
